import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Edit, Trash2, Building, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import DeleteConfirmDialog from './DeleteConfirmDialog';

const emptyCentre = {
  name: '',
  code: '',
  address: '',
  contact_person: '',
  contact_phone: '',
  capacity: '',
  status: 'active',
  remarks: '',
};

/**
 * Manages the centres (sub-units) belonging to a single project.
 * Admin-only writes are enforced by the backend.
 */
const CentresManager = ({ open, onOpenChange, project }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyCentre);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteDlg, setDeleteDlg] = useState({ open: false, item: null });

  const fetchAll = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const r = await api.get('/centres', { params: { project_id: project.id } });
      setItems(r.data || []);
    } catch {
      toast.error('Failed to load centres');
    } finally {
      setLoading(false);
    }
  }, [project]);

  useEffect(() => {
    if (open) {
      setForm(emptyCentre);
      setEditing(null);
      setShowForm(false);
      fetchAll();
    }
  }, [open, fetchAll]);

  const startAdd = () => { setForm(emptyCentre); setEditing(null); setShowForm(true); };
  const startEdit = (c) => {
    setForm({
      name: c.name || '',
      code: c.code || '',
      address: c.address || '',
      contact_person: c.contact_person || '',
      contact_phone: c.contact_phone || '',
      capacity: c.capacity ?? '',
      status: c.status || 'active',
      remarks: c.remarks || '',
    });
    setEditing(c);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Centre name is required'); return; }
    try {
      const payload = {
        project_id: project.id,
        name: form.name.trim(),
        code: form.code || null,
        address: form.address || null,
        contact_person: form.contact_person || null,
        contact_phone: form.contact_phone || null,
        capacity: form.capacity ? Number(form.capacity) : null,
        status: form.status,
        remarks: form.remarks || null,
      };
      if (editing) {
        await api.put(`/centres/${editing.id}`, payload);
        toast.success('Centre updated');
      } else {
        await api.post('/centres', payload);
        toast.success('Centre added');
      }
      setShowForm(false);
      setForm(emptyCentre);
      setEditing(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/centres/${deleteDlg.item.id}`);
      toast.success('Centre deleted');
      setDeleteDlg({ open: false, item: null });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="centres-manager-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="w-5 h-5 text-emerald-700" />
              Centres of {project?.name || 'Project'}
              {project?.code && <span className="ml-1 text-sm font-mono text-slate-500">({project.code})</span>}
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center justify-between mt-2 mb-3">
            <p className="text-sm text-slate-600">
              {items.length} centre{items.length === 1 ? '' : 's'} • add as many as needed under this project.
            </p>
            {!showForm && (
              <Button size="sm" onClick={startAdd} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-centre-button">
                <Plus className="w-4 h-4 mr-1" /> Add Centre
              </Button>
            )}
          </div>

          {showForm && (
            <form onSubmit={submit} className="bg-emerald-50/60 border border-emerald-200 rounded-lg p-4 mb-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Centre Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g., Age Home - Thongjao" data-testid="centre-name" />
                </div>
                <div>
                  <Label>Code</Label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g., AH-THG" />
                </div>
                <div className="md:col-span-2">
                  <Label>Address</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Full address" />
                </div>
                <div>
                  <Label>Contact Person</Label>
                  <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="10-digit mobile" />
                </div>
                <div>
                  <Label>Capacity (inmates)</Label>
                  <Input type="number" min="0" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="e.g., 25" />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Remarks</Label>
                  <Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</Button>
                <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-centre-btn">
                  {editing ? 'Update Centre' : 'Add Centre'}
                </Button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-700"></div></div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 bg-slate-50 rounded-lg border border-dashed border-slate-300">
              <Building className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500 text-sm">No centres yet. Click &quot;Add Centre&quot; to create the first one.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">#</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Code</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Contact</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Capacity</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((c, i) => (
                    <tr key={c.id} data-testid="centre-row" className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">{c.name}</td>
                      <td className="px-3 py-2 font-mono text-slate-600">{c.code || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {c.contact_person ? <div>{c.contact_person}</div> : null}
                        {c.contact_phone ? <div className="text-xs text-slate-500">{c.contact_phone}</div> : (c.contact_person ? null : '—')}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{c.capacity ?? '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'
                        }`}>{c.status}</span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { onOpenChange(false); navigate(`/centres/${c.id}`); }} className="h-7 w-7 p-0 text-emerald-700 hover:bg-emerald-50" data-testid={`manage-centre-${c.id}`} title="Manage inmates & staff">
                            <Users className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => startEdit(c)} className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50" data-testid={`edit-centre-${c.id}`}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteDlg({ open: true, item: c })} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" data-testid={`delete-centre-${c.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDlg.open}
        onOpenChange={(o) => setDeleteDlg({ open: o, item: o ? deleteDlg.item : null })}
        onConfirm={doDelete}
        title="Delete Centre"
        description="This action cannot be undone. Any inmates and centre-staff linked to this centre will need to be reassigned."
        itemName={deleteDlg.item?.name || ''}
      />
    </>
  );
};

export default CentresManager;
